import { useState, useEffect, useContext } from 'react';
import { Table, Button, Input, Form, Modal, Select, Spin, Popconfirm, message, Pagination, Drawer } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import axios from 'axios';
import { LoginContext } from '../../../context/loginContext';

const { Option } = Select;

const CCuentaAdministrador = () => {
    const [cuentas, setCuentas] = useState([]);
    const [localidades, setLocalidades] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingCuenta, setEditingCuenta] = useState(null);
    const [form] = Form.useForm();
    const { user, selectedEmpresa } = useContext(LoginContext);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 10;

    // Estado para manejar los filtros
    const [filters, setFilters] = useState({ cue_nombre: '', cue_cuit: '' });
    const [drawerVisible, setDrawerVisible] = useState(false);

    useEffect(() => {
        fetchCuentas();
        fetchLocalidades();
    }, [currentPage, filters]);  // Incluimos los filtros en las dependencias

    const fetchCuentas = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: currentPage,
                limit: pageSize,
                ...(filters.cue_cuit && { cue_cuit: filters.cue_cuit }), // Solo incluye si no está vacío
                ...(filters.cue_nombre && { cue_nombre: filters.cue_nombre }),
            });
            const url = `${process.env.REACT_APP_API_URL}/cuenta/empresa/${selectedEmpresa.emp_id}?${queryParams.toString()}`;
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(url, config);
            setCuentas(response.data.cuentas);
            setTotalItems(response.data.total);
        } catch (error) {
            console.error('Error obteniendo cuentas:', error);
            message.error('Error al cargar las cuentas');
        } finally {
            setLoading(false);
        }
    };

    const fetchLocalidades = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/localidad`, config);
            setLocalidades(response.data);
        } catch (error) {
            console.error('Error obteniendo localidades:', error);
        }
    };

    const handleAdd = () => {
        setEditingCuenta(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleEdit = (record) => {
        setEditingCuenta(record);
        form.setFieldsValue({
            cue_nombre: record.cue_nombre,
            cue_domicilio: record.cue_domicilio,
            cue_telefono: record.cue_telefono,
            cue_mail: record.cue_mail,
            cue_localidad: record.cue_localidad,
            cue_tipocuenta: record.cue_tipocuenta,
            cue_cuit: record.cue_cuit
        });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/cuenta/${id}`;
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(url, config);
            message.success('Cuenta eliminada correctamente');
            fetchCuentas();
        } catch (error) {
            console.error('Error eliminando la cuenta:', error);
            message.error('Error al eliminar la cuenta');
        }
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const data = { ...values, cue_empresa: selectedEmpresa?.emp_id };
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            if (editingCuenta) {
                await axios.put(`${process.env.REACT_APP_API_URL}/cuenta/${editingCuenta.cue_id}`, data, config);
                message.success('Cuenta actualizada correctamente');
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/cuenta`, data, config);
                message.success('Cuenta creada correctamente');
            }

            setModalVisible(false);
            fetchCuentas();
        } catch (error) {
            console.error('Error guardando la cuenta:', error);
            message.error('Error al guardar la cuenta');
        }
    };

    const handleFilterSubmit = (values) => {
        setFilters(values);
        setCurrentPage(1); // Reiniciar a la primera página
        setDrawerVisible(false);
    };

    const columns = [
        { title: 'Nombre', dataIndex: 'cue_nombre', key: 'cue_nombre' },
        { title: 'CUIT', dataIndex: 'cue_cuit', key: 'cue_cuit' },
        {
            title: 'Localidad',
            dataIndex: 'cue_localidad',
            key: 'cue_localidad',
            render: (localidadId) => {
                const localidad = localidades.find(loc => loc.loc_id === localidadId);
                return localidad ? localidad.loc_nombre : localidadId;
            }
        },
        {
            title: 'Tipo Cuenta',
            dataIndex: 'cue_tipocuenta',
            key: 'cue_tipocuenta',
            render: (tipo) => {
                const tipoMap = {
                    'CF': 'Consumidor Final',
                    'EX': 'Exento',
                    'MON': 'Monotributo',
                    'RI': 'Responsable Inscripto'
                };
                return tipoMap[tipo] || tipo;
            }
        },
        {
            title: 'Acciones', key: 'action',
            render: (_, record) => (
                <>
                    <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} style={{ marginRight: 8 }} />
                    {/* <Popconfirm title="¿Eliminar esta cuenta?" onConfirm={() => handleDelete(record.cue_id)} okText="Sí" cancelText="No">
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm> */}
                </>
            ),
        },
    ];

    return (
        <div>
            <h1>Cuentas</h1>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>Agregar Cuenta</Button>
            <Button
                type="default"
                icon={<FilterOutlined />}
                onClick={() => setDrawerVisible(true)}
                style={{ marginBottom: 16, marginLeft: 8 }}
            >
                Filtros
            </Button>
            {loading ? <Spin size="large" style={{ display: 'block', margin: '20px auto' }} /> : (
                <>
                    <Table
                        dataSource={cuentas}
                        columns={columns}
                        rowKey="cue_id"
                        pagination={false}
                    />
                    <Pagination
                        current={currentPage}
                        total={totalItems}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        style={{ marginTop: 16, textAlign: 'center' }}
                    />
                </>
            )}
 <Modal 
                title={editingCuenta ? "Editar Cuenta" : "Nueva Cuenta"} 
                visible={modalVisible} 
                onOk={handleSubmit} 
                onCancel={() => setModalVisible(false)} 
                okText={editingCuenta ? "Actualizar" : "Crear"} 
                cancelText="Cancelar"
                width={800}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="cue_nombre" label="Nombre" rules={[{ required: true, message: 'Ingrese el nombre' }]}>
                        <Input placeholder="Nombre de la cuenta" />
                    </Form.Item>
                    
                    <Form.Item name="cue_cuit" label="CUIT" rules={[{ required: true, message: 'Ingrese el CUIT' }]}>
                        <Input placeholder="CUIT (ej: 30123456789)" />
                    </Form.Item>
                    
                    <Form.Item name="cue_domicilio" label="Domicilio" rules={[{ required: true, message: 'Ingrese el domicilio' }]}>
                        <Input placeholder="Domicilio de la cuenta" />
                    </Form.Item>
                    
                    <Form.Item name="cue_telefono" label="Teléfono" rules={[{ message: 'Ingrese el teléfono' }]}>
                        <Input placeholder="Teléfono de contacto" />
                    </Form.Item>
                    
                    <Form.Item name="cue_mail" label="Email" rules={[{ message: 'Ingrese el email' }, { type: 'email', message: 'Formato de email inválido' }]}>
                        <Input placeholder="Correo electrónico" />
                    </Form.Item>
                    
                    <Form.Item name="cue_localidad" label="Localidad" rules={[{ required: true, message: 'Seleccione la localidad' }]}>
                        <Select placeholder="Seleccione una localidad" showSearch optionFilterProp="children">
                            {localidades.map((loc) => (
                                <Option key={loc.loc_id} value={loc.loc_id}>{loc.loc_nombre}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    
                    <Form.Item name="cue_tipocuenta" label="Tipo de Cuenta" rules={[{ required: true, message: 'Seleccione el tipo de cuenta' }]}>
                        <Select placeholder="Seleccione un tipo">
                            <Option value="CF">Consumidor Final (CF)</Option>
                            <Option value="EX">Exento (EX)</Option>
                            <Option value="MON">Monotributo (MON)</Option>
                            <Option value="RI">Responsable Inscripto (RI)</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Drawer para filtros */}
            <Drawer
                title="Filtros"
                visible={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                width={350}
            >
                <Form layout="vertical" onFinish={handleFilterSubmit} initialValues={filters}>
                    <Form.Item label="CUIT/CUIL" name="cue_cuit">
                        <Input placeholder="30111111110" />
                    </Form.Item>
                    <Form.Item label="Nombre" name="cue_nombre">
                        <Input placeholder="Nombre S.A." />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">Aplicar Filtros</Button>
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default CCuentaAdministrador;